import posthog from "posthog-js";
import { applicationConfig } from "@/config";
import type { RootDataStructure } from "@/features/nbi/utils/parser/models";
import type { DocumentValidationIssue, Reference } from "@/features/nbi/types/documentTypes";
import type { ListChatsResponse, ChatDetail } from "@/features/ask/types/chatHistory";
import type { CurrentUser } from "@/features/login/types";
import type { DocumentTypeSettings, DocumentType, SampleData, BoundingBox } from "@/features/schema-management/types";
import type { BankerInterfaceSettings } from "@/features/nbi/types/settingsTypes";

export interface BackendValidationField {
  id: string;
  entityType: string;
  entityId: number;
  fieldName: string;
  currentValue: string | null;
  confidenceScore: number;
  verified: boolean;
  edited: boolean;
  documentId: number | null;
  requiresValidation: boolean;
  references: Pick<Reference, "fileId" | "fileName" | "boundingBoxes" | "validationStatus" | "validationStatusReason" | "reasoning" | "confidenceScore">;
}

const API_BASE_URL = applicationConfig.apiUri;

function getAuthToken(): string | null {
    return localStorage.getItem("accessToken");
}

async function fetchRaw(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = getAuthToken();

    const headers = new Headers(options.headers);
    if (token) {
        headers.set("Authorization", `Bearer ${token}`);
    }

    if (!(options.body instanceof FormData)) {
        headers.set("Content-Type", "application/json");
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });

    if (!response.ok) {
        if (response.status === 401) {
            localStorage.removeItem("accessToken");
            throw new Error("Unauthorized");
        }

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `code ${response.status}: ${response.statusText}`);
    }

    return response;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const response = await fetchRaw(endpoint, options);

    if (response.status === 204) {
        return undefined as T;
    }

    return response.json();
}

export const api = {
    getCurrentUser: async (): Promise<CurrentUser> => {
        return fetchApi("/api/user/me", { method: "GET" });
    },

    getDocumentTypeSettings: async (): Promise<DocumentTypeSettings> => {
        return fetchApi("/api/settings/document-types", { method: "GET" });
    },

    patchDocumentTypeSettings: async (update: Partial<DocumentTypeSettings>): Promise<DocumentTypeSettings> => {
        return fetchApi("/api/settings/document-types", {
            method: "PATCH",
            body: JSON.stringify(update),
        });
    },

    getBankerInterfaceSettings: async (): Promise<BankerInterfaceSettings> => {
        return fetchApi("/api/settings/banker-interface", { method: "GET" });
    },

    patchBankerInterfaceSettings: async (update: Partial<BankerInterfaceSettings>): Promise<BankerInterfaceSettings> => {
        return fetchApi("/api/settings/banker-interface", {
            method: "PATCH",
            body: JSON.stringify(update),
        });
    },

    createApplication: async (): Promise<{ applicationId: number }> => {
        return fetchApi("/api/applications", {
            method: "POST",
        });
    },

    uploadDocument: async (
        applicationId: number,
        file: File,
        forceOcr?: boolean
    ): Promise<{ documentId: number; fileName: string; status: string }> => {
        const formData = new FormData();
        formData.append("file", file);

        const resolvedForceOcr = forceOcr ?? (
            import.meta.env.VITE_FORCE_OCR !== undefined
                ? import.meta.env.VITE_FORCE_OCR === "true"
                : undefined
        );
        if (resolvedForceOcr !== undefined) {
            formData.append("force_ocr", String(resolvedForceOcr));
        }

        try {
            return await fetchApi(`/api/applications/${applicationId}/documents`, {
                method: "POST",
                body: formData,
            });
        } catch (error) {
            posthog.captureException(error instanceof Error ? error : new Error(String(error)), {
                source: "frontend",
                context: "uploadDocument",
                application_id: applicationId,
                file_name: file.name,
                file_size: file.size,
                file_type: file.type,
            });
            throw error;
        }
    },

    getSplitSuggestions: async (
        applicationId: number,
        documentId: number
    ): Promise<{ totalPages: number; intervals: Array<{ title: string; fromPage: number; toPage: number }> }> => {
        return fetchApi(`/api/applications/${applicationId}/documents/${documentId}/split-preview`, {
            method: "GET",
        });
    },

    splitDocument: async (
        applicationId: number,
        documentId: number,
        intervals: Array<{ title: string; fromPage: number; toPage: number }>
    ): Promise<{ documents: Array<{ documentId: number; fileName: string; status: string }> }> => {
        return fetchApi(`/api/applications/${applicationId}/documents/${documentId}/split`, {
            method: "POST",
            body: JSON.stringify({ intervals }),
        });
    },

    dismissDocumentSplit: async (
        applicationId: number,
        documentId: number
    ): Promise<{ documentId: number; fileName: string; status: string }> => {
        return fetchApi(`/api/applications/${applicationId}/documents/${documentId}/process`, {
            method: "POST",
        });
    },

    uploadDocumentType: async (
        file: File
    ): Promise<{ documentTypeId: number; fileName: string; status: string }> => {
        const formData = new FormData();
        formData.append("file", file);
        return fetchApi("/api/document-types", { method: "POST", body: formData });
    },

    getDocumentType: async (
        id: number
    ): Promise<{ documentType: DocumentType }> => {
        return fetchApi(`/api/document-types/${id}`);
    },

    getDocumentTypeStatus: async (
        id: number
    ): Promise<{ documentType: DocumentType }> => {
        return fetchApi(`/api/document-types/${id}/status`);
    },

    getAllDocumentTypes: async (): Promise<{ documentTypes: DocumentType[] }> => {
        return fetchApi("/api/document-types");
    },

    getPendingDocumentTypes: async (): Promise<{ documentTypes: Array<{ id: number; fileName: string; status: string }> }> => {
        return fetchApi("/api/document-types/pending");
    },

    approveDocumentType: async (id: number): Promise<{ documentType: DocumentType }> => {
        return fetchApi(`/api/document-types/${id}/approve`, { method: "POST" });
    },

    patchDocumentType: async (
        id: number,
        patch: { confidenceLevelThreshold?: number; documentType?: string }
    ): Promise<{ documentType: DocumentType }> => {
        return fetchApi(`/api/document-types/${id}`, {
            method: "PATCH",
            body: JSON.stringify(patch),
        });
    },

    runFieldExtraction: async (
        id: number,
        params: { fieldName: string; fieldType: string; description: string }
    ): Promise<{ value: string; confidenceScore: number | null; boundingBoxes: BoundingBox[]; extractedValues: Record<string, unknown>; columnNames?: string[]; columnTypes?: string[] }> => {
        const fieldTypeMap: Record<string, string> = {
            "Text": "str",
            "Date": "str",
            "Table": "table",
            "Number": "number",
            "Yes / No": "bool",
        };
        const response = await fetchApi<{ extracted: Record<string, unknown>; field: { fieldName: string; fieldType: string; columnNames?: string[]; columnTypes?: string[] } }>(
            `/api/document-types/${id}/schema/field/extract`,
            {
                method: "POST",
                body: JSON.stringify({
                    fieldName: params.fieldName,
                    description: params.description,
                    fieldType: fieldTypeMap[params.fieldType] ?? "str",
                }),
            }
        );

        const camelKey = params.fieldName.replace(/_([a-z0-9])/g, (_, l) => l.toUpperCase());

        if (params.fieldType === "Table") {
            const ref = response.extracted[`${camelKey}Reference`] as Record<string, unknown> | undefined;

            return {
                value: "Table Content",
                confidenceScore: (ref?.confidenceScore as number) ?? null,
                boundingBoxes: (ref?.boundingBoxes as BoundingBox[]) ?? [],
                extractedValues: response.extracted,
                columnNames: response.field.columnNames,
                columnTypes: response.field.columnTypes,
            };
        }

        const ref = response.extracted[`${camelKey}Reference`] as Record<string, unknown> | undefined;
        console.log("field extraction", { camelKey, value: response.extracted[camelKey], ref, extracted: response.extracted });
        return {
            value: response.extracted[camelKey] != null ? String(response.extracted[camelKey]) : "",
            confidenceScore: (ref?.confidenceScore as number) ?? null,
            boundingBoxes: (ref?.boundingBoxes as BoundingBox[]) ?? [],
            extractedValues: response.extracted,
        };
    },

    saveDocumentTypeField: async (
        id: number,
        params: { fieldName: string; fieldType: string; description: string; extractedValues: Record<string, unknown>; columnNames?: string[]; columnTypes?: string[] }
    ): Promise<void> => {
        const fieldTypeMap: Record<string, string> = {
            "Text": "str",
            "Date": "str",
            "Table": "table",
            "Number": "number",
            "Yes / No": "bool",
        };
        await fetchApi<unknown>(`/api/document-types/${id}/schema/field`, {
            method: "POST",
            body: JSON.stringify({
                fieldName: params.fieldName,
                description: params.description,
                fieldType: fieldTypeMap[params.fieldType] ?? "str",
                ...(params.columnNames && { columnNames: params.columnNames }),
                ...(params.columnTypes && { columnTypes: params.columnTypes }),
                extractedValues: params.extractedValues,
            }),
        });
    },

    getDocumentTypeBytes: async (id: number): Promise<Blob> => {
        const response = await fetchRaw(`/api/document-types/${id}/bytes`);
        return response.blob();
    },

    deleteDocumentType: async (id: number): Promise<void> => {
        await fetchRaw(`/api/document-types/${id}`, { method: "DELETE" });
    },

    deleteDocumentTypeField: async (id: number, fieldName: string): Promise<void> => {
        await fetchApi<unknown>(`/api/document-types/${id}/schema/field/${fieldName}`, { method: "DELETE" });
    },

    deleteDocumentTypeSample: async (documentTypeId: number, sampleId: number): Promise<void> => {
        await fetchRaw(`/api/document-types/${documentTypeId}/samples/${sampleId}`, { method: "DELETE" });
    },

    getDocumentTypeSamples: async (
        documentTypeId: number
    ): Promise<{ samples: Array<{ id: number; fileName: string }> }> => {
        return fetchApi(`/api/document-types/${documentTypeId}/samples`);
    },

    uploadDocumentTypeSample: async (
        documentTypeId: number,
        file: File
    ): Promise<{ sample: { id: number; fileName: string; status: string } }> => {
        const formData = new FormData();
        formData.append("file", file);
        return fetchApi(`/api/document-types/${documentTypeId}/samples`, { method: "POST", body: formData });
    },

    getDocumentTypeSample: async (
        documentTypeId: number,
        sampleId: number
    ): Promise<{ sample: SampleData }> => {
        return fetchApi(`/api/document-types/${documentTypeId}/samples/${sampleId}`);
    },

    getDocumentTypeSampleBytes: async (documentTypeId: number, sampleId: number): Promise<Blob> => {
        const response = await fetchRaw(`/api/document-types/${documentTypeId}/samples/${sampleId}/bytes`);
        return response.blob();
    },

    patchDocumentSampleField: async (
        documentTypeId: number,
        sampleId: number,
        fieldName: string,
        value: string | number | boolean | unknown[]
    ): Promise<{ sample: SampleData }> => {
        return fetchApi(`/api/document-types/${documentTypeId}/samples/${sampleId}/fields/${fieldName}`, {
            method: "PATCH",
            body: JSON.stringify({ value }),
        });
    },

    submitApplication: async (
        applicationId: number,
        applicationData: string
    ): Promise<{ success: boolean; message: string }> => {
        return fetchApi(`/api/applications/${applicationId}/submit`, {
            method: "POST",
            body: JSON.stringify({ applicationData }),
        });
    },

    getApplicationStatus: async (
        applicationId: number
    ): Promise<{
        applicationId: number;
        userId: number;
        createdAt: string;
        data: {
            amounts: { propertyMarketValue: number; loanAmount: number; downPayment: number };
            [key: string]: unknown;
        };
        outcomeStatus: string;
    }> => {
        return fetchApi(`/api/applications/${applicationId}`, {
            method: "GET",
        });
    },

    getDocumentProcessData: async (applicationId: number, documentId: number): Promise<RootDataStructure> => {
        return fetchApi(`/api/applications/${applicationId}/documents/${documentId}/data`, {
            method: "GET",
        });
    },

    getDocumentsStatus: async (
        applicationId: number
    ): Promise<{
        documents: Array<{
            id: number;
            fileName: string;
            status: string;
            size?: number;
            documentValidation: DocumentValidationIssue[];
            processingError?: Record<string, unknown> | null;
        }>;
    }> => {
        const response = await fetchApi<{
            documents: Array<{
                id: number;
                fileName: string;
                status: string;
                size?: number;
                documentValidation: DocumentValidationIssue[];
                processingError?: Record<string, unknown> | null;
            }>;
        }>(`/api/applications/${applicationId}/documents/status`, {
            method: "GET",
        });

        return {
            documents: response.documents.map(doc => ({
                id: doc.id,
                fileName: doc.fileName,
                status: doc.status,
                size: doc.size,
                documentValidation: doc.documentValidation,
                processingError: doc.processingError,
            })),
        };
    },

    getDocumentContent: async (
        documentId: number
    ): Promise<{
        fileName: string;
        mimeType: string;
        fileSize: number;
        contentBase64: string;
    }> => {
        return fetchApi(`/api/documents/${documentId}/info`, {
            method: "GET",
        });
    },

    listChats: async (): Promise<ListChatsResponse> => {
        return fetchApi("/api/chats", {
            method: "GET",
        });
    },

    getChat: async (chatId: string, options?: { signal?: AbortSignal }): Promise<ChatDetail> => {
        return fetchApi(`/api/chats/${chatId}`, {
            method: "GET",
            signal: options?.signal,
        });
    },

    deleteDocument: async (documentId: number): Promise<void> => {
        await fetchApi(`/api/documents/${documentId}`, { method: "DELETE" });
    },

    deleteChat: async (chatId: string): Promise<void> => {
        return fetchApi(`/api/chats/${chatId}`, {
            method: "DELETE",
        });
    },

    getApplicationValidation: async (
        applicationId: number
    ): Promise<BackendValidationField[]> => {
        return fetchApi(`/api/applications/${applicationId}/validation`, {
            method: "GET",
        });
    },

    validateApplicationFields: async (applicationId: number): Promise<void> => {
        return fetchApi(`/api/applications/${applicationId}/validate-fields`, {
            method: "POST",
        });
    },

    verifyValidationField: async (
        applicationId: number,
        fieldId: string
    ): Promise<void> => {
        return fetchApi(`/api/applications/${applicationId}/validation/${fieldId}/verify`, {
            method: "PUT",
            body: JSON.stringify({ verified: true }),
        });
    },

    getDocumentBytes: async (
        applicationId: number,
        documentId: number
    ): Promise<{
        contentBase64: string;
        mimeType: string;
    }> => {
        const token = getAuthToken();
        const headers = new Headers();
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }

        const response = await fetch(
            `${API_BASE_URL}/api/applications/${applicationId}/documents/${documentId}/bytes`,
            {
                method: "GET",
                headers,
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem("accessToken");
                throw new Error("Unauthorized");
            }
            throw new Error(`Failed to fetch document bytes: ${response.statusText}`);
        }

        const blob = await response.blob();
        const mimeType = response.headers.get("content-type") || "application/octet-stream";

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                const commaIndex = base64String.indexOf(",");
                const contentBase64 = commaIndex !== -1 ? base64String.slice(commaIndex + 1) : base64String;
                resolve({ contentBase64, mimeType });
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    },

    createApplicant: async (applicationId: number): Promise<{ id: number}> => {
        return fetchApi(`/api/applicants`, {
            method: "POST",
            body: JSON.stringify({ applicationId }),
        });
    },

    deleteApplicant: async (applicantId: number): Promise<void> => {
        return fetchApi(`/api/applicants/${applicantId}`, {
            method: "DELETE",
        });
    },

    createEmployment: async (applicantId: number): Promise<{ id: number }> => {
        return fetchApi("/api/employments", {
            method: "POST",
            body: JSON.stringify({ applicantId }),
        });
    },

    deleteEmployment: async (employmentId: number): Promise<void> => {
        return fetchApi(`/api/employments/${employmentId}`, {
            method: "DELETE",
        });
    },

    createPaygIncome: async (applicantId: number): Promise<{ id: number }> => {
        return fetchApi("/api/payg-incomes", {
            method: "POST",
            body: JSON.stringify({ applicantId }),
        });
    },

    deletePaygIncome: async (paygIncomeId: number): Promise<void> => {
        return fetchApi(`/api/payg-incomes/${paygIncomeId}`, {
            method: "DELETE",
        });
    },

    createRentalIncome: async (applicantId: number): Promise<{ id: number }> => {
        return fetchApi("/api/rental-incomes", {
            method: "POST",
            body: JSON.stringify({ applicantId }),
        });
    },

    deleteRentalIncome: async (rentalIncomeId: number): Promise<void> => {
        return fetchApi(`/api/rental-incomes/${rentalIncomeId}`, {
            method: "DELETE",
        });
    },

    createSelfEmployedIncome: async (applicantId: number): Promise<{ id: number }> => {
        return fetchApi("/api/self-employed-incomes", {
            method: "POST",
            body: JSON.stringify({ applicantId }),
        });
    },

    deleteSelfEmployedIncome: async (selfEmployedIncomeId: number): Promise<void> => {
        return fetchApi(`/api/self-employed-incomes/${selfEmployedIncomeId}`, {
            method: "DELETE",
        });
    },

    createNonpaygIncome: async (applicantId: number): Promise<{ id: number }> => {
        return fetchApi("/api/nonpayg-incomes", {
            method: "POST",
            body: JSON.stringify({ applicantId }),
        });
    },

    deleteNonpaygIncome: async (nonpaygIncomeId: number): Promise<void> => {
        return fetchApi(`/api/nonpayg-incomes/${nonpaygIncomeId}`, {
            method: "DELETE",
        });
    },

    deletePaygPayCycle: async (paygIncomeId: number, cycleId: string): Promise<void> => {
        return fetchApi(`/api/payg-incomes/${paygIncomeId}/pay-cycles/${cycleId}`, {
            method: "DELETE",
        });
    },

    patchApplicant: async (
        applicantId: number,
        data: {
            given_name?: string;
            family_name?: string;
            id_value?: string;
            date_of_birth?: string;
            is_foreign_or_non_resident?: boolean;
            identity_type?: string;
            address?: {
                address_line?: string;
                suburb?: string;
                city?: string;
                state?: string;
                post_code?: string;
                country?: string;
            };
        }
    ): Promise<Record<string, unknown>> => {
        return fetchApi(`/api/applicants/${applicantId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

    patchEmployment: async (
        employmentId: number,
        data: {
            employer_name?: string;
            business_abn?: string;
            job_title?: string;
            employment_type?: string;
            employment_start_date?: string;
            employment_end_date?: string;
        }
    ): Promise<Record<string, unknown>> => {
        return fetchApi(`/api/employments/${employmentId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

    patchPaygIncome: async (
        paygIncomeId: number,
        data: {
            payg_type?: string;
            pay_frequency?: string;
            document_issue_date?: string;
            pay_cycles?: Array<{
                id?: string;
                pay_date?: string;
                gross_pay?: number;
                net_pay?: number;
                base_wage?: number;
                ytd_gross?: number;
                overtime_amount?: number;
                commission_amount?: number;
                bonus_amount?: number;
            }>;
            applicant_id?: number;
        }
    ): Promise<Record<string, unknown>> => {
        return fetchApi(`/api/payg-incomes/${paygIncomeId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

    patchRentalIncome: async (
        rentalIncomeId: number,
        data: {
            rental_type?: string;
            property_address?: string;
            document_issue_date?: string;
            rental_amount?: number;
            payment_frequency?: string;
            appraised_rent_range_low?: number;
            total_income_for_period?: number;
            currency?: string;
            currency_country?: string;
            applicant_id?: number;
        }
    ): Promise<Record<string, unknown>> => {
        return fetchApi(`/api/rental-incomes/${rentalIncomeId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

    patchSelfEmployedIncome: async (
        selfEmployedIncomeId: number,
        data: {
            business_structure?: string;
            business_abn?: string;
            currency?: string;
            currency_country?: string;
            financial_year_end?: string;
            taxable_income?: number;
            taxes_on_income?: number;
            applicant_id?: number;
        }
    ): Promise<Record<string, unknown>> => {
        return fetchApi(`/api/self-employed-incomes/${selfEmployedIncomeId}`, {
            method: "PATCH",
            body: JSON.stringify(data),
        });
    },

};
